import React from 'react';
import * as PhosphorIcons from '@phosphor-icons/react';

const LanguageDetectorViewResult = ({result}) => {
	// Return a fallback UI if result is missing or malformed
	if (!result || typeof result !== 'object' || !result.analysis) {
		return (
			<div className="mt-4 p-4 border rounded shadow-sm bg-base-100">
				<div className="flex items-center gap-2 text-error">
					<PhosphorIcons.Warning size={20} weight="bold"/>
					<p className="font-semibold">No valid language detection data available</p>
				</div>
			</div>
		);
	}

	const {analysis} = result;

	// Helper function to format confidence score
	const formatConfidence = (confidence) => {
		if (typeof confidence === 'number') {
			return `${(confidence * 100).toFixed(1)}%`;
		}
		return <span className="text-base-content/50 italic">N/A</span>;
	};

	// Helper function to format value for display
	const formatValue = (value) => {
		if (value === null || value === undefined) {
			return <span className="text-base-content/50 italic">N/A</span>;
		}
		if (typeof value === 'boolean') {
			return value ? 'Yes' : 'No';
		}
		if (Array.isArray(value)) {
			return value.map((lang, index) => (
				<div key={index} className="flex items-center gap-2">
					<span>{lang?.name}</span>
					<span className="text-base-content/70">({formatConfidence(lang.confidence)})</span>
				</div>
			));
		}
		return String(value);
	};

	// Extract primary language and secondary languages from analysis
	const primaryLanguage = analysis?.primary_language;
	const secondaryLanguages = analysis?.secondary_languages?.detected || [];

	return (
		<div className="mt-4 space-y-4">
			{/* Primary Language Section */}
			<div className="bg-base-100 border border-base-300 rounded-lg p-4 shadow-sm">
				<div className="flex items-center gap-3 mb-4">
					<PhosphorIcons.Globe size={24} weight="bold" className="text-primary"/>
					<h3 className="text-lg font-semibold">Primary Language</h3>
					<span className="badge badge-primary">{formatConfidence(primaryLanguage?.confidence)}</span>
				</div>
				<div className="grid grid-cols-2 gap-4">
					<div>
						<p className="text-sm text-base-content/70">Language</p>
						<p className="font-medium">{primaryLanguage?.name}</p>
					</div>
					<div>
						<p className="text-sm text-base-content/70">Code</p>
						<p className="font-medium">{primaryLanguage?.code}</p>
					</div>
				</div>
			</div>

			{/* Secondary Languages Section */}
			{secondaryLanguages.length > 0 && (
				<div className="bg-base-100 border border-base-300 rounded-lg p-4 shadow-sm">
					<div className="flex items-center gap-3 mb-4">
						<PhosphorIcons.Translate size={24} weight="bold" className="text-secondary"/>
						<h3 className="text-lg font-semibold">Secondary Languages</h3>
					</div>
					<div className="space-y-2">
						{secondaryLanguages.map((lang, index) => (
							<div key={index} className="flex items-center justify-between p-2 bg-base-200/50 rounded">
								<span className="font-medium">{lang?.name}</span>
								<span className="badge badge-secondary">{formatConfidence(lang.confidence)}</span>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default LanguageDetectorViewResult;
